import CardGridView from "@/components/CardGridView";
import { Calendar, Flag, Pencil, Users, MapPin } from "lucide-react";
import { useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Link, useParams } from "react-router";
import { Code, CodeService } from "@/services/CodeService";
import { observationUrlBuilder } from "@/lib/url-builder";
import { Observation, ObservationService } from "@/services/ObservationService";
import DeleteDialog from "../DeleteDialog";
import { Loading } from "../Loading";
import { CacheService } from "@/services/CacheService";
import { appConfig } from "@/lib/AppConfig";
import { formatDateDisplayString } from "@/lib/string-utils";

interface ObservationDetails {
  id: string;
  name: string;
  typeCode: Code;
  observedById: string | null;
  observedAt: string | null;
  attachmentUrl: string | null;
  createdDate: Date
  areaName: string | null;
  areaId: string | null;
  featureName: string | null;
  featureTypeName: string | null;
  observedByName: string | null;
}

interface ObservationCardGridViewProps {
  pageTitle: string;
  observations: Observation[];
  createObservationUrl?: string
}

const ObservationCardGridView: React.FC<ObservationCardGridViewProps> = ({ pageTitle, observations, createObservationUrl }) => {
  const params = useParams();
  
  const [observationDetails, setObservationDetails] = useState<ObservationDetails[]>([]);
  const [deletedObservtionId, setDeletedObservationId] = useState<string | null>(null);
  const [showDeleteObservationDialog, setShowDeleteObservationDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initializeData = async () => {
      const [codes] = await Promise.all([
        CacheService.get("allcodes", appConfig.resourceTypes.code, async () => {
          return CodeService.getCodes2();
        }),
      ]);

      const record: Record<string, Code> = codes.reduce((acc, item) => {
        acc[item.id] = item;
        return acc;
      }, {} as Record<string, Code>);

      const observationInfo = observations
          .map((observation) => ({
            id: observation.id!,
            name: observation.name,
            typeCode: record[observation.typeCodeId!] ?? {
              id: "",
              name: "N/A",
              type: "N/A",
            },
            observedAt: formatDateDisplayString(observation.observedAt),
            observedByName: observation.observedByName,
            observedById: observation.observedById,
            attachmentUrl: observation.attachmentUrl,
            createdDate: observation.observedAt ? new Date(observation.observedAt) : new Date(),
            areaName: observation.areaName,
            areaId: observation.areaId ?? null,
            featureName: observation.featureName,
            featureTypeName: observation.featureTypeName,
          }))
          .sort((a, b) => b.createdDate.getTime() - a.createdDate.getTime())

      setObservationDetails(observationInfo);
      setIsLoading(false);
    };
    initializeData();
  }, [params.siteId, observations])

  return (
    <>
      <Loading isLoading={isLoading} message="Loading observations..." />
      <CardGridView
        title={pageTitle}
        editPageUrl={(site: string, id: string) =>
          observationUrlBuilder(params.tenantId!, site, id)
        }
        items={observationDetails}
        createPageUrl={createObservationUrl}
        onDeleteItem={async (observation) => {
          setDeletedObservationId(observation.id);
          setShowDeleteObservationDialog(true);
        }}
        renderContentItem={(item) => (
          <div className="flex">
            {item.attachmentUrl && (
              <img
                src={item.attachmentUrl}
                alt="Attachment"
                className="w-16 h-16 object-cover rounded mr-4"
              />
            )}
            <div className="flex-1 space-y-1">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Flag className="h-4 w-4 text-gray-400" />
                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium`}>
                    {item.typeCode?.name}
                  </span>
                </div>
                <div className="flex items-center">
                  {item.featureName && (<>
                    <MapPin className="h-4 w-4 text-gray-400" />
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium`}>
                      {item.featureName}
                    </span>
                  </>)}

                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium`}>
                    {item.observedAt}
                  </span>
                </div>
                <Badge variant="secondary" className="text-xs">
                  <Users className="h-3 w-3 mr-1" />
                  {item.observedByName}
                </Badge>
              </div>
            </div>
          </div>
        )}
        tableHeaders={[
          { id: "name", child: <>Name</> },
          { id: "typeCode", child: <>Type</> },
          { id: "area", child: <>Area</> },
          { id: "feature", child: <>Feature</> },
          { id: "featureType", child: <>Feature Type</> },
          { id: "actions", child: "Edit" },
        ]}
        tableContentItem={(observation) => [
          <span key="name">{observation.name}</span>,
          <span key="typeCode">{observation.typeCode?.name}</span>,
          <span key="area">{observation.areaName}</span>,
          <span key="feature">{observation.featureName}</span>,
          <span key="featureType">{observation.featureTypeName}</span>,
          <Link to={observationUrlBuilder(params.tenantId!, params.siteId!, observation.id!)}>
            <Pencil key="actions" className="h-4 w-4" aria-label={`Edit Area`} />
          </Link>
          
        ]}
      />      
      <DeleteDialog
        onDelete={async () => {
          if (!deletedObservtionId) {
            alert("No feature selected for deletion");
            return;
          }
          await ObservationService.delete(deletedObservtionId);
          setObservationDetails(observationDetails.filter((a) => a.id !== deletedObservtionId));
          setDeletedObservationId(null);
        }}
        deleteConfirmationText={
          <p>Are you sure you want to delete this observation?</p>
        }
        isOpen={showDeleteObservationDialog}
        onOpenChange={setShowDeleteObservationDialog}
      />
    </>
  );
};

export default ObservationCardGridView;
